/**
 * Scope-flexible crew chat (D1) — the same Vari-crew-only thread, addressable
 * by TASK (legacy) or by PROJECT (chat moved up to the project). One panel
 * (CrewChatPanel) drives both; these hooks pick the endpoint + cache key from
 * the scope. The response shape is identical (TaskMessageListResponse).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  TaskMessage,
  TaskMessageCreate,
  TaskMessageListResponse,
} from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';
import { uploadTaskMedia } from '@/api/attachments';
import { uploadProjectMedia } from '@/api/activity';

export type ChatScope = { taskId: string } | { projectId: string };

function scopeBase(scope: ChatScope): string {
  return 'taskId' in scope ? `/tasks/${scope.taskId}/messages` : `/projects/${scope.projectId}/messages`;
}
export function chatScopeKey(scope: ChatScope) {
  return 'taskId' in scope
    ? (['task-messages', scope.taskId] as const)
    : (['project-messages', scope.projectId] as const);
}

export function useChatMessages(scope: ChatScope | undefined) {
  return useQuery<TaskMessageListResponse>({
    queryKey: scope ? chatScopeKey(scope) : ['chat-messages', 'none'],
    enabled: !!scope,
    queryFn: () => apiCall<TaskMessageListResponse>(scopeBase(scope!)),
    staleTime: 10_000,
    // Poll while on screen so teammates' messages land without a reload; pauses
    // when the tab is hidden. Your own sends appear immediately via the mutation.
    refetchInterval: 10_000,
  });
}

export function useSendChatMessage(scope: ChatScope) {
  const qc = useQueryClient();
  return useMutation<TaskMessage, Error, TaskMessageCreate>({
    mutationFn: (body) => apiCall<TaskMessage>(scopeBase(scope), { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatScopeKey(scope) });
      qc.invalidateQueries({ queryKey: ['my-notifications'] });
    },
  });
}

/** Upload a chat attachment into the right owner for the scope. */
export function uploadChatMedia(scope: ChatScope, file: File) {
  return 'taskId' in scope ? uploadTaskMedia(scope.taskId, file) : uploadProjectMedia(scope.projectId, file);
}
